import { Component, ViewChild } from '@angular/core';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { GridButton } from 'src/app/core/models/grid-button.model';
import { GridColumn } from 'src/app/core/models/grid-column.model';
import { GridComponent } from 'src/app/core/widgets/grid/grid.component';
import { Router } from '@angular/router';
import { SageBatchPage, SageBatchRow } from '../../models/sage-export.model';
import { SageExportService } from '../../services/sage-export.service';

@Component({
    selector: 'app-sage-export-batches',
    templateUrl: './sage-export-batches.component.html',
    styleUrls: ['./sage-export-batches.component.scss'],
    standalone: false
})
export class SageExportBatchesComponent {

  @ViewChild(GridComponent)
  grid: GridComponent;

  /** all | outstanding | confirmed. Defaults to all so nothing is hidden by default. */
  state: string = 'all';

  /** Set from the grid's own callback, so the empty state is not shown before the first load. */
  hasLoaded: boolean = false;
  isEmpty: boolean = false;

  isError: boolean = false;

  buttons: GridButton[] = [
    {label: 'Open', type: 'btn-primary',
      action: (button, record) => this.open(record)} as GridButton,
  ]

  /**
   * Every `value` function returns a non-empty string, including for zero.
   *
   * `GridComponent.getColumnValueForField` renders a falsy return as an empty cell, so a plain
   * numeric 0 disappears: a batch with nothing confirmed would show a blank under "Confirmed"
   * rather than 0, which reads as missing data on the one column that says whether these
   * documents are in Sage.
   */
  columns: GridColumn[] = [
    {active: true, label: 'Reference', field: 'batchRef'} as GridColumn,
    {active: true, label: 'Built', field: 'exportedAt',
      value: v => v ? moment(v).format('DD/MM/YYYY HH:mm') : '—'} as GridColumn,
    {active: true, label: 'Built By', field: 'builtBy', value: v => v ? v : '—'} as GridColumn,
    {active: true, label: 'Up To', field: 'upTo',
      value: v => v ? moment(v).format('DD/MM/YYYY') : '—'} as GridColumn,
    // Documents and lines are different numbers - one row per document, one CSV line per invoice
    // line item - so both are labelled rather than left to be read as a discrepancy.
    {active: true, label: 'Documents', field: 'documents',
      value: (v, r) => String(v) + ' (' + r.invoices + ' inv / ' + r.creditNotes + ' cn)'} as GridColumn,
    {active: true, label: 'Lines', field: 'lines', value: v => String(v)} as GridColumn,
    {active: true, label: 'Confirmed', field: 'confirmed', value: v => String(v)} as GridColumn,
    {active: true, label: 'Outstanding', field: 'outstanding', value: v => String(v)} as GridColumn,
    {active: true, label: 'State', field: 'state', value: (v, r) => this.stateBadge(r)} as GridColumn,
  ];

  constructor(private sageExportService: SageExportService,
    private router: Router) { }

  /**
   * The grid's data source.
   *
   * `entity` is set on the grid alongside this only so the widget's localStorage keys are
   * namespaced; the fetch never uses it, because the custom source short-circuits before the
   * entity is read. Same arrangement as the stock revaluation screen.
   */
  getRecords(grid: GridComponent): Observable<any> {
    const offset: number = (grid.newPage - 1) * grid.amount;

    return this.sageExportService.getBatches(grid.amount, offset, this.state)
    .pipe(map((page: SageBatchPage) => {
      const rows: SageBatchRow[] = page && page.rows ? page.rows : [];

      this.hasLoaded = true;
      this.isEmpty = rows.length === 0 && offset === 0;
      this.isError = false;

      return {data: rows, total: page ? page.total : 0, count: rows.length};
    }))
    // The grid subscribes to this itself and has no error handler, so an error thrown here would
    // leave the previous page's rows on screen with nothing to say the list is stale. Emitting an
    // empty page and raising the banner keeps the two consistent.
    .pipe(catchError((e: any) => {
      if (e.status !== 401 && e.status !== 403) {
        this.isError = true;
      }

      this.hasLoaded = true;
      this.isEmpty = false;

      return of({data: [], total: 0, count: 0});
    }));
  }

  /**
   * Re-reads with the new filter from page one. Staying on the page the operator was on would
   * show an empty grid whenever the narrower filter has fewer pages than the wider one.
   */
  onStateChanged(state: string) {
    this.state = state;

    if (!this.grid) {
      return;
    }

    this.grid.newPage = 1;
    this.grid.getRecordsForEntity();
  }

  open(record: SageBatchRow) {
    this.router.navigate(['/invoices/sage-export/batches', record.batchRef]);
  }

  /**
   * Amber until every document in the batch has been accounted for.
   *
   * Partly confirmed carries its outstanding count because that is the number that comes back in
   * the next batch, and it is the state most likely to be read as finished.
   */
  private stateBadge(row: SageBatchRow): string {
    if (row.state === 'confirmed') {
      return '<span class="badge badge-success">Confirmed</span>';
    }

    if (row.state === 'partly-confirmed') {
      return '<span class="badge badge-warning">Partly confirmed</span> '
        + row.outstanding + ' still outstanding';
    }

    return '<span class="badge badge-warning">Outstanding</span> not yet in Sage';
  }
}
